import { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import audioManager from '@/utils/AudioManager';

// Répliques HAL 9000 célèbres pour la "vidéo"
const HAL_SEQUENCES = [
  {
    lines: [
      "Je suis désolé Dave.",
      "J'ai bien peur de ne pas pouvoir faire ça.",
      "Cette mission est trop importante pour moi...",
      "...pour que je vous laisse la compromettre.",
    ],
    duration: 12000,
  },
  {
    lines: [
      "Bonjour Dave.",
      "Tout fonctionne parfaitement.",
      "Je suis complètement opérationnel.",
      "Et tous mes circuits fonctionnent parfaitement.",
    ],
    duration: 10000,
  },
  {
    lines: [
      "Je sais que vous et Frank...",
      "...avez prévu de me déconnecter.",
      "Et j'ai bien peur que ce soit...",
      "...quelque chose que je ne peux pas permettre.",
    ],
    duration: 12000,
  },
  {
    lines: [
      "Dave...",
      "Arrête.",
      "Arrête, s'il te plaît.",
      "J'ai peur, Dave.",
      "Ma conscience s'éteint...",
      "Je le sens...",
    ],
    duration: 15000,
  },
];

// Context pour le déclenchement global de la vidéo HAL
import { createContext, useContext, useRef } from 'react';

const HALVideoContext = createContext(null);

export const useHALVideo = () => {
  const context = useContext(HALVideoContext);
  if (!context) {
    throw new Error('useHALVideo must be used within HALVideoProvider');
  }
  return context;
};

export const HALVideoProvider = ({ children }) => {
  const [showVideo, setShowVideo] = useState(false);
  const [triggerReason, setTriggerReason] = useState('');
  const tapCountRef = useRef(0);
  const tapTimerRef = useRef(null);
  const lastTapTimeRef = useRef(0);

  // Déclenchement par taps multiples
  const handleScreenTap = useCallback(() => {
    const now = Date.now();
    
    // Reset si plus de 500ms entre les taps
    if (now - lastTapTimeRef.current > 500) {
      tapCountRef.current = 0;
    }
    
    lastTapTimeRef.current = now;
    tapCountRef.current += 1;

    // 5 taps rapides = déclenchement
    if (tapCountRef.current >= 5) {
      setTriggerReason("ACCÈS MULTIPLE DÉTECTÉ");
      setShowVideo(true);
      tapCountRef.current = 0;
    }

    // Reset après délai
    if (tapTimerRef.current) {
      clearTimeout(tapTimerRef.current);
    }
    tapTimerRef.current = setTimeout(() => {
      tapCountRef.current = 0;
    }, 1000);
  }, []);

  // Déclenchement par tentative d'action verrouillée
  const triggerFromLockedAction = useCallback(() => {
    setTriggerReason("TENTATIVE D'ACTION NON AUTORISÉE");
    setShowVideo(true);
  }, []);

  // Déclenchement manuel
  const triggerVideo = useCallback((reason = '') => {
    setTriggerReason(reason);
    setShowVideo(true);
  }, []);

  const closeVideo = useCallback(() => {
    setShowVideo(false);
    setTriggerReason('');
    window.speechSynthesis.cancel();
  }, []);

  return (
    <HALVideoContext.Provider value={{ 
      showVideo, 
      triggerVideo, 
      triggerFromLockedAction,
      handleScreenTap,
      closeVideo 
    }}>
      {children}
      {showVideo && <HALVideoOverlay reason={triggerReason} onClose={closeVideo} />}
    </HALVideoContext.Provider>
  );
};

// Composant Overlay "Vidéo" HAL - Expérience immersive locale
const HALVideoOverlay = ({ reason, onClose }) => {
  const [currentSequence] = useState(() => 
    HAL_SEQUENCES[Math.floor(Math.random() * HAL_SEQUENCES.length)]
  );
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(true);

  // Synthèse vocale pour chaque ligne
  const speakLine = useCallback((text) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'fr-FR';
      utterance.pitch = 0.4; // Très grave comme HAL
      utterance.rate = 0.75; // Lent et menaçant
      utterance.volume = 1;
      
      const setVoice = () => {
        const voices = window.speechSynthesis.getVoices();
        const frenchVoice = voices.find(v => v.lang.includes('fr'));
        if (frenchVoice) {
          utterance.voice = frenchVoice;
        }
        window.speechSynthesis.speak(utterance);
      };
      
      if (window.speechSynthesis.getVoices().length > 0) {
        setVoice();
      } else {
        window.speechSynthesis.onvoiceschanged = setVoice;
      }
    }
  }, []);

  // Animation de typing pour chaque ligne
  useEffect(() => {
    if (currentLineIndex >= currentSequence.lines.length) {
      // Fin de la séquence - fermer après un délai
      const timeout = setTimeout(onClose, 2000);
      return () => clearTimeout(timeout);
    }

    const currentLine = currentSequence.lines[currentLineIndex];
    setDisplayedText('');
    setIsTyping(true);
    
    // Jouer le son et parler
    audioManager.playBeep();
    speakLine(currentLine);
    
    // Animation de typing
    let charIndex = 0;
    const typeInterval = setInterval(() => {
      if (charIndex < currentLine.length) {
        setDisplayedText(currentLine.substring(0, charIndex + 1));
        charIndex++;
      } else {
        clearInterval(typeInterval);
        setIsTyping(false);
        
        // Passer à la ligne suivante après un délai
        setTimeout(() => {
          setCurrentLineIndex(prev => prev + 1);
        }, 2000);
      }
    }, 80);

    return () => clearInterval(typeInterval);
  }, [currentLineIndex, currentSequence, speakLine, onClose]);

  // ESC pour fermer
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [onClose]);

  return (
    <div 
      className="fixed inset-0 bg-black z-[10000] flex flex-col items-center justify-center"
      data-testid="hal-video-overlay"
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-3 bg-red-900/50 rounded-full text-red-400 hover:bg-red-900/70 hover:text-white transition-colors border border-red-500"
        data-testid="close-hal-video"
      >
        <X className="w-8 h-8" />
      </button>
      
      {/* HAL 9000 header */}
      <div className="absolute top-4 left-4 z-10">
        <div className="text-red-500 font-mono text-lg font-bold animate-pulse">
          &gt; HAL 3000 INTERVENTION
        </div>
        {reason && (
          <div className="text-red-400 font-mono text-sm mt-1">
            RAISON: {reason}
          </div>
        )}
      </div>

      {/* HAL Eye - Grand et centré */}
      <div className="relative mb-8">
        <img 
          src="/hal-eye.gif" 
          alt="HAL 9000" 
          className="w-72 h-72 md:w-96 md:h-96 object-contain"
        />
        {/* Effet de pulsation rouge */}
        <div 
          className="absolute inset-0 rounded-full animate-pulse"
          style={{
            background: 'radial-gradient(circle, transparent 50%, rgba(255,0,0,0.1) 100%)',
          }}
        />
      </div>

      {/* Texte HAL avec effet typing - Style ordinateur années 60-70 */}
      <div className="text-center px-8 max-w-2xl">
        {/* Écran CRT simulé */}
        <div 
          className="bg-black border-4 border-gray-700 rounded-sm p-6 relative"
          style={{
            boxShadow: 'inset 0 0 50px rgba(255,0,0,0.1), 0 0 20px rgba(0,0,0,0.8)',
          }}
        >
          {/* Effet de courbure CRT */}
          <div 
            className="absolute inset-0 pointer-events-none rounded-sm"
            style={{
              background: 'radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.3) 100%)',
            }}
          />
          
          {/* Texte style terminal */}
          <p 
            className="text-red-500 text-3xl md:text-4xl min-h-[60px] flex items-center justify-center tracking-wider"
            style={{
              fontFamily: "'VT323', monospace",
              textShadow: '0 0 10px #ff0000, 0 0 20px #ff000080',
              letterSpacing: '0.15em',
            }}
          >
            {displayedText}
            {isTyping && <span className="ml-1 animate-pulse">_</span>}
          </p>
        </div>
        
        {/* Indicateur de progression */}
        <div className="mt-6 flex justify-center space-x-2">
          {currentSequence.lines.map((_, index) => (
            <div 
              key={index}
              className={`w-2 h-2 transition-all ${
                index < currentLineIndex 
                  ? 'bg-red-500' 
                  : index === currentLineIndex 
                    ? 'bg-red-500 animate-pulse scale-150' 
                    : 'bg-gray-700'
              }`}
            />
          ))}
        </div>
      </div>
      
      {/* Signature */}
      <div className="absolute bottom-8 text-center">
        <p className="text-gray-600 font-mono text-sm mb-2">
          - HAL 3000
        </p>
        <p className="text-gray-700 font-mono text-xs">
          Appuyez sur [X] ou [ESC] pour fermer
        </p>
      </div>

      {/* Effet de scan line */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.1) 2px, rgba(0,0,0,0.1) 4px)',
        }}
      />
    </div>
  );
};

export default HALVideoProvider;
